import { useCallback, useMemo } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import { useSortableListInputContext } from ".";
import { useFormatMessage } from "../../Intl/Context";
import Button from "../../Buttons/Button";
import Icon from "../../Utils/Icon";
import Collapse from "../../Utils/Collapse";
import OptionDescriptionButton from "../OptionDescriptionButton";
import OptionDescription from "../OptionDescription";

const SortableListOptionsListOption = props => {
  const { option, reserveDescriptionButtonSpace } = props;
  const { selectedItems, addToSelectedItems, addItemToOptions, maximumOptions } =
    useSortableListInputContext();

  const isSelected = selectedItems.some(({ id }) => id === option.id);

  const t = useFormatMessage();
  const selected = t(intlMessages.selected);

  const maxItemsSelected = useMemo(
    () => maximumOptions && selectedItems.length >= maximumOptions,
    [maximumOptions, selectedItems.length]
  );
  const isDisabled = !isSelected && maxItemsSelected;

  const handleClick = useCallback(() => {
    if (isSelected) return addItemToOptions(option);
    if (maxItemsSelected) return;

    addToSelectedItems(option);
  }, [addItemToOptions, addToSelectedItems, isSelected, maxItemsSelected, option]);

  // The option button shares its row with the description button, so it fills the
  // remaining space instead of taking the whole row width.
  const className = classNames(
    "padding-sm",
    "btn",
    "btn-shadow",
    "btn-sm",
    "border",
    "w-100",
    {
      "btn-primary": !isSelected && !maxItemsSelected,
      disabled: isDisabled,
      "bg-light": isDisabled,
      "border-primary": !isSelected && !isDisabled,
      "border-gray": isDisabled,
      "bg-success": isSelected,
      "border-success": isSelected,
      "text-white": isSelected
    }
  );

  // Options without a description render an invisible description button to keep the
  // same width as the others, as the `invisible` class keeps the button's space while
  // hiding it from screen readers and keyboard navigation.
  const descriptionButtonClassName = classNames({ invisible: !option.description });

  const iconClassName = classNames("fa", "margin-none", {
    "fa-plus": !isSelected,
    "fa-minus": isSelected,
    "text-primary": !isSelected,
    "text-success": isSelected
  });

  // The description button can't be nested in the option button, as it would both be
  // invalid HTML and select the option when tapped, so they're rendered side by side.
  return (
    <Collapse>
      <div className="sortable-list-options-list__option margin-y-xxs">
        <div className="d-flex align-items-center">
          <div className="flex-1">
            <Button type="button" onClick={handleClick} className={className}>
              <span className="d-flex align-items-center w-100">
                {isSelected && (
                  <span>
                    <Icon className="fa fa-check text-white margin-right-xs" />
                    <span className="sr-only">{selected}</span>
                  </span>
                )}

                <span className="flex-1">{option.name}</span>

                {!isDisabled && (
                  <span className="sortable-list-options-list-option-toggle-icon bg-primary-inverse margin-left-xs">
                    <Icon className={iconClassName} />
                  </span>
                )}
              </span>
            </Button>

            {option.description && <OptionDescription description={option.description} />}
          </div>

          {reserveDescriptionButtonSpace && (
            <OptionDescriptionButton className={descriptionButtonClassName} />
          )}
        </div>
      </div>
    </Collapse>
  );
};

export default SortableListOptionsListOption;

const intlMessages = defineMessages({
  selected: {
    id: "inputs.sortableList.optionsListOption.selected",
    defaultMessage: "Option sélectionnée"
  }
});
